<template>
	<div>
		<div>
			<h2 class="mb-4 font-bold">{{ type }} {{ $t('news') }}</h2>
			<div class="mb-4">
				<label class="block mb-2" for="title">{{ $t('title') }}</label>
				<input
					type="text"
					v-model="news.title"
					id="title"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				/>
				<div v-if="$v.news.title.$dirty">
					<div class="text-sm text-red-500 mt-2" v-if="!$v.news.title.required">
						{{ $t('title_required') }}
					</div>
					<div
						class="text-sm text-red-500 mt-2"
						v-if="!$v.news.title.maxLength"
					>
						{{ $t('title_validation') }}
					</div>
				</div>
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="category">{{ $t('categorias') }}</label>
				<select
					v-model="news.category_id"
					ref="category"
					id="category"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				>
					<option
						v-for="(category, index) in categories"
						:value="category.id"
						:key="index"
					>
						{{ category.category_name }}
					</option>
				</select>
				<div v-if="$v.news.category_id.$dirty">
					<div
						class="text-sm text-red-500 mt-2"
						v-if="!$v.news.category_id.required"
					>
						{{ $t('category_required') }}
					</div>
				</div>
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="currentcountry">País</label>
				<select
					v-model="news.country_id"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				>
					<option
						v-for="(country, index) in countries"
						:value="country.id"
						:key="index"
					>
						{{ country.name }}
					</option>
				</select>
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="category">{{ $t('featured') }}</label>
				<toggle-button v-model="news.featured" color="#68ace1" :sync="true" />
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="description">{{
					$t('description')
				}}</label>
				<textarea
					v-model="news.description"
					id="description"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				></textarea>
				<div v-if="$v.news.description.$dirty">
					<div
						class="text-sm text-red-500 mt-2"
						v-if="!$v.news.description.required"
					>
						{{ $t('description_required') }}
					</div>
					<div
						class="text-sm text-red-500 mt-2"
						v-if="!$v.news.description.maxLength"
					>
						{{ $t('description_validation') }}
					</div>
				</div>
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="body">{{ $t('body') }}</label>
				<quill-editor
					class="h-40 mb-16"
					id="body"
					v-model="news.body"
					:content="news.body"
					:options="editorOption"
				>
				</quill-editor>
				<div v-if="$v.news.body.$dirty">
					<div class="text-sm text-red-500 mt-2" v-if="!$v.news.body.required">
						{{ $t('body_required') }}
					</div>
				</div>
			</div>

			<h3 class="max-w-md" v-if="image || news.image">
				{{ (image && image.name) || (news.image && news.image.file_name) }}
			</h3>
			<img-upload
				:image="image"
				@upload-image="img_upload"
				@reset-image="img_reset"
			/>
			<div v-if="$v.image.$dirty">
				<div class="text-sm text-red-500 mt-2" v-if="!$v.image">
					{{ $t('image_validation') }}
				</div>
			</div>

			<div class="mb-4">
				<label class="block mb-2">{{ $t('publication_date') }}</label>
				<datepicker
					v-model="news.publishing_date"
					:full-month-name="true"
					:language="es"
					:inline="true"
					:monday-first="true"
					:disabled-dates="disabledDates"
				>
				</datepicker>
				<div v-if="$v.news.publishing_date.$dirty">
					<div
						class="text-sm text-red-500 mt-2"
						v-if="!$v.news.publishing_date.required"
					>
						{{ $t('publication_date_required') }}
					</div>
				</div>
			</div>

			<label>{{ $t('Roles') }}</label>
			<dual-list
				v-if="DualListOptions"
				id="vueduallist"
				:options="DualListOptions"
			/>
			<div v-if="$v.DualListOptions.selectedItems.$dirty">
				<div
					class="text-sm text-red-500 mt-2"
					v-if="!$v.DualListOptions.selectedItems.required"
				>
					{{ $t('roles_required') }}
				</div>
			</div>

			<div class="flex justify-end">
				<ButtonBlack class="mt-4" @click.native="submit">
					<div v-if="loading">
						<pulse-loader
							:loading="loading"
							:color="'#fff'"
							:size="'10px'"
						></pulse-loader>
					</div>
					<div v-else>
						{{ buton_text }}
					</div>
				</ButtonBlack>
			</div>
			<div v-if="error" class="mt-4">
				<p class="text-sm">{{ $t('server_error') }}</p>
				<p class="text-sm text-red-500" v-html="server_error"></p>
			</div>
		</div>
	</div>
</template>

<script>
import datepicker from 'vuejs-datepicker'
import { es } from 'vuejs-datepicker/dist/locale'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import DualList from '@/components/DualList.vue'
import { required, maxLength, integer } from 'vuelidate/lib/validators'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { single, uploadImage } from '@/mixins'
import { ToggleButton } from 'vue-js-toggle-button'
import ImgUpload from '@/components/ImgUpload.vue'
import { fetchNewsCategoriesSelect } from '@/apollo/newsCategories'
import { fetchCountriesSelect } from '@/apollo/countries'
import { fetchRolesSelect } from '@/apollo/roles'
import { fetchNews, insertNews, updateNews } from '@/apollo/news'

export default {
	mixins: [single, uploadImage],
	components: {
		datepicker,
		DualList,
		PulseLoader,
		quillEditor,
		ToggleButton,
		ImgUpload
	},
	data() {
		return {
			disabledDates: {
				to: this.id ? null : this.$moment().subtract(1, 'days').toDate()
			},
			news: {
				title: '',
				category_id: null,
				country_id: null,
				featured: false,
				description: '',
				roles: this.id ? null : [],
				body: '',
				image: null,
				publishing_date: new Date()
			},
			roles: null,
			categories: [],
			countries: [],
			es: es,
			editorOption: {
				theme: 'snow',
				placeholder: '',
				modules: {
					toolbar: [
						['bold', 'italic', 'underline', 'strike'],
						[{ list: 'ordered' }, { list: 'bullet' }],
						[{ header: [1, 2, 3, 4, 5, 6, false] }],
						[{ color: [] }, { background: [] }],
						[{ font: [] }],
						[{ align: [] }],
						['link'],
						['clean']
					]
				}
			},
			image: null
		}
	},
	apollo: {
		categories: fetchNewsCategoriesSelect(),
		countries: fetchCountriesSelect(),
		roles: fetchRolesSelect(),
		news() {
			const query = fetchNews(this.id)
			query.skip = this.id == null
			return query
		}
	},
	watch: {
		roles: function (data) {
			if (this.news.roles !== null && this.DualListOptions === null) {
				this.loadRolesList(data, this.news.roles)
			}
		},
		news: function (data) {
			if (
				this.id != null &&
				this.roles != null &&
				this.DualListOptions === null
			) {
				this.loadRolesList(this.roles, data.roles)
			}
		}
	},
	methods: {
		img_reset() {
			this.image ? (this.image = null) : (this.news.image = null)
		},
		img_upload(img) {
			this.image = img
		},
		date_now(date) {
			this.publishing_date = this.$moment(date).format('YYYY-MM-DD')
		},
		loadRolesList(roles, selected) {
			this.loadDualList(
				roles,
				selected
					? selected
							.map(org => roles.find(elem => elem.id == org.id))
							.filter(elem => elem != null)
					: selected
			)
		},

		async updateSingleNews() {
			if (this.image)
				this.news.image = await this.uploadImage(this.news, 'news', this.image)
			const mutationData = {
				newsId: this.id,
				newsData: this.getNewsUpdateData(),
				newsRoles: this.DualListOptions.selectedItems
			}
			await this.$apollo.mutate(updateNews(mutationData))
		},

		async insertSingleNews() {
			if (this.image)
				this.news.image = await this.uploadImage(this.news, 'news', this.image)
			const mutationData = {
				newsData: this.getNewsInsertData(),
				newsRoles: this.DualListOptions.selectedItems
			}
			await this.$apollo.mutate(insertNews(mutationData))
		},

		getNewsUpdateData() {
			return {
				title: this.news.title,
				category_id: this.news.category_id,
				country_id: this.news.country_id,
				featured: this.news.featured,
				description: this.news.description,
				body: this.news.body,
				publishing_date: this.news.publishing_date,
				image_id: this.news.image ? this.news.image.id : null
			}
		},

		getNewsInsertData() {
			return {
				title: this.news.title,
				category_id: this.news.category_id,
				country_id: this.news.country_id,
				featured: this.news.featured,
				description: this.news.description,
				body: this.news.body,
				publishing_date: this.news.publishing_date,
				image: this.news.image ? { data: this.news.image } : undefined
			}
		},

		async submit() {
			this.startLoadingState()
			this.$v.$touch()
			if (!this.$v.$invalid) {
				;(this.id ? this.updateSingleNews() : this.insertSingleNews())
					.then(() => this.stopLoadingState())
					.catch(error => this.setError(error))
			} else {
				this.setError(this.$t('form_validation'))
			}
		}
	},
	validations() {
		return {
			image: function (img) {
				return img || this.bonus.image
			},
			news: {
				title: {
					required,
					maxLength: maxLength(100)
				},
				description: {
					required,
					maxLength: maxLength(300)
				},
				body: {
					required
				},
				category_id: {
					required,
					integer
				},
				featured: {
					required
				},
				publishing_date: {
					required
				}
			},
			DualListOptions: {
				selectedItems: {
					required
				}
			}
		}
	}
}
</script>
